// Layer renderers for the report card canvas.
// All sizes are in canvas-px and must be multiplied by `scale` before use in CSS.
import type { ReactNode } from "react"
import type { Layer } from "./types"

const SAMPLE_GRADES = [
  { name: "English Language", obtained: 85, total: 100, percent: 85, grade: "A", result: "Pass" },
  { name: "Mathematics",      obtained: 78, total: 100, percent: 78, grade: "B+", result: "Pass" },
  { name: "Science",          obtained: 92, total: 100, percent: 92, grade: "A+", result: "Pass" },
  { name: "Islamiat",         obtained: 70, total: 75,  percent: 93, grade: "A+", result: "Pass" },
  { name: "Urdu",             obtained: 68, total: 100, percent: 68, grade: "B",  result: "Pass" },
]

type Tpl = {
  schoolName?: string
  schoolTagline?: string
  headerColor?: string
  accentColor?: string
  columns?: Record<string, boolean>
  signatures?: string[]
}

// Convert an RMS image path to our S3 key
const rmsUrlToKey = (url: string) =>
  "rc-assets/" + url.replace(/^\/+/, "").replace(/^report-card-templates\//, "")

export const renderLayer = (
  layer: Layer,
  scale: number,
  tpl: Tpl,
  assetUrls: Record<string, string>,
  onNeedUrl?: (key: string) => void,
): ReactNode => {
  const headerBg = tpl.headerColor || "#0f172a"
  const accent   = tpl.accentColor || "#1a56db"
  const col      = tpl.columns || {}
  const allOff   = !col.showObtained && !col.showTotal && !col.showPercent && !col.showGrade && !col.showRemarks
  const S = {
    obt: allOff || col.showObtained !== false,
    tot: allOff || col.showTotal !== false,
    pct: allOff || col.showPercent !== false,
    grd: allOff || col.showGrade !== false,
    res: allOff || col.showRemarks !== false,
  }

  const s = (v: number) => v * scale

  // ── helpers ─────────────────────────────────────────────────────────────────
  const rp = (layer._rmsProps || {}) as Record<string, any>

  switch (layer.type) {

    // ── Primitive types ──────────────────────────────────────────────────────

    case "image": {
      const url = layer.src ? assetUrls[layer.src] : null
      if (layer.src && !url && onNeedUrl) onNeedUrl(layer.src)
      const fit = layer.fit || "contain"
      return url
        ? <img src={url} style={{ width: "100%", height: "100%", objectFit: fit, display: "block" }} alt="" />
        : <div style={{ width: "100%", height: "100%", background: "#e2e8f0", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <i className="ti ti-photo" style={{ fontSize: s(24), color: "#94a3b8" }} />
          </div>
    }

    case "text":
      return (
        <div style={{
          width: "100%", height: "100%",
          fontFamily: layer.fontFamily || "inherit",
          fontSize: s(layer.fontSize || 14),
          fontWeight: layer.fontWeight || "normal",
          fontStyle: layer.fontStyle || "normal",
          color: layer.color || "#0f172a",
          textAlign: (layer.textAlign as any) || "left",
          lineHeight: layer.lineHeight || 1.2,
          letterSpacing: s(layer.letterSpacing || 0),
          overflow: "hidden", padding: s(4), opacity: layer.opacity ?? 1,
        }}>
          {layer.content || "Text"}
        </div>
      )

    case "dynamic-text":
      return (
        <div style={{
          width: "100%", height: "100%",
          fontFamily: layer.fontFamily || "inherit",
          fontSize: s(layer.fontSize || 14),
          fontWeight: layer.fontWeight || "normal",
          color: layer.color || "#0f172a",
          textAlign: (layer.textAlign as any) || "left",
          lineHeight: layer.lineHeight || 1.2,
          overflow: "hidden", padding: s(4), opacity: layer.opacity ?? 1,
        }}>
          {layer.prefix || ""}<span style={{ color: "#6366f1" }}>{`{{${layer.field || "field"}}}`}</span>{layer.suffix || ""}
        </div>
      )

    case "shape": {
      const isCircle = layer.shape === "ellipse"
      return (
        <div style={{
          width: "100%", height: "100%",
          background: layer.fill || "transparent",
          border: `${s(layer.strokeWidth || 1)}px solid ${layer.stroke || "transparent"}`,
          borderRadius: isCircle ? "50%" : s(layer.borderRadius || 0),
          opacity: layer.opacity ?? 1,
        }} />
      )
    }

    case "line":
      return (
        <div style={{
          width: "100%", height: s(layer.thickness || 2),
          background: layer.color || "#0f172a",
          borderRadius: layer.rounded ? s(layer.thickness || 2) / 2 : 0,
          position: "absolute", top: "50%", transform: "translateY(-50%)",
          opacity: layer.opacity ?? 1,
        }} />
      )

    // ── Our own structured types ─────────────────────────────────────────────

    case "header":
      return (
        <div style={{
          width: "100%", height: "100%",
          background: layer.headerColor || headerBg, color: "#fff",
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          padding: `${s(8)}px ${s(16)}px`, boxSizing: "border-box",
        }}>
          <div style={{ fontWeight: 800, fontSize: s(18), letterSpacing: s(1.5), textTransform: "uppercase" }}>
            {tpl.schoolName || "The Eon Academy"}
          </div>
          {tpl.schoolTagline && <div style={{ fontSize: s(9), opacity: 0.75, marginTop: s(2) }}>{tpl.schoolTagline}</div>}
          <div style={{ fontSize: s(10), marginTop: s(4), opacity: 0.85 }}>Final Term Evaluation (2025–2026)</div>
        </div>
      )

    case "student_info":
      return (
        <div style={{
          width: "100%", height: "100%",
          display: "flex", gap: s(8), padding: s(6),
          boxSizing: "border-box", border: `${s(1)}px solid ${headerBg}25`, background: "#f9fafb",
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: s(13), color: "#111827", marginBottom: s(2) }}>Zarmeen Kamal</div>
            <div style={{ fontSize: s(9), color: "#374151", lineHeight: 1.6 }}>
              <div>GR No: 435004 &nbsp;|&nbsp; Roll No: 12</div>
              <div>Class: Montessori II &nbsp;|&nbsp; Section: Diamonds</div>
            </div>
          </div>
          {layer.showPhoto && (
            <div style={{
              width: s(56), height: s(70), background: "#e5e7eb",
              border: `${s(1)}px solid #d1d5db`, borderRadius: s(4),
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <i className="ti ti-user" style={{ fontSize: s(24), color: "#9ca3af" }} />
            </div>
          )}
        </div>
      )

    case "subject_table": {
      const thBg = layer.headerColor || headerBg
      const fs   = s(8.5)
      const pad  = `${s(3)}px ${s(5)}px`
      return (
        <div style={{ width: "100%", height: "100%", overflow: "hidden" }}>
          <table style={{ width: "100%", height: "100%", borderCollapse: "collapse", fontSize: fs, tableLayout: "fixed" }}>
            <thead>
              <tr style={{ background: thBg, color: "#fff" }}>
                <th style={{ padding: pad, textAlign: "left", fontWeight: 700, width: "36%" }}>Subject</th>
                {S.obt && <th style={{ padding: pad, textAlign: "center", width: "13%" }}>Marks</th>}
                {S.grd && <th style={{ padding: pad, textAlign: "center", width: "13%" }}>Grade</th>}
              </tr>
            </thead>
            <tbody>
              {SAMPLE_GRADES.map((g, i) => (
                <tr key={g.name} style={{ background: i % 2 === 0 ? "#fff" : "#f9fafb" }}>
                  <td style={{ padding: pad, borderBottom: `${s(0.5)}px solid #e5e7eb`, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.name}</td>
                  {S.obt && <td style={{ padding: pad, textAlign: "center", borderBottom: `${s(0.5)}px solid #e5e7eb` }}>{g.obtained}/{g.total}</td>}
                  {S.grd && <td style={{ padding: pad, textAlign: "center", borderBottom: `${s(0.5)}px solid #e5e7eb`, color: accent, fontWeight: 700 }}>{g.grade}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )
    }

    case "grade_key":
      return (
        <div style={{ width: "100%", height: "100%", padding: s(6), boxSizing: "border-box", border: `${s(1)}px solid ${headerBg}30`, borderRadius: s(2) }}>
          <div style={{ fontSize: s(8), fontWeight: 700, color: headerBg, marginBottom: s(4), textTransform: "uppercase" }}>Grade Key</div>
          <div style={{ display: "flex", gap: s(8), flexWrap: "wrap" }}>
            {[["A+","90-100"],["A","80-89"],["B+","70-79"],["B","60-69"],["C","50-59"],["D","40-49"],["F","Below 40"]].map(([g, r]) => (
              <div key={g} style={{ fontSize: s(8) }}>
                <span style={{ fontWeight: 700, color: accent }}>{g}</span>
                <span style={{ color: "#6b7280", marginLeft: s(2) }}>{r}</span>
              </div>
            ))}
          </div>
        </div>
      )

    case "signatures": {
      const sigs = tpl.signatures?.length ? tpl.signatures : ["Class Teacher", "Principal", "Date"]
      return (
        <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "flex-end", borderTop: `${s(1)}px solid ${headerBg}30`, paddingTop: s(4), boxSizing: "border-box" }}>
          {sigs.map((sig, i) => (
            <div key={i} style={{ flex: 1, textAlign: "center" }}>
              <div style={{ borderTop: `${s(1)}px solid #0f172a`, marginInline: s(12), paddingTop: s(3), fontSize: s(9), color: "#374151" }}>{sig}</div>
            </div>
          ))}
        </div>
      )
    }

    case "attendance":
      return (
        <div style={{ width: "100%", height: "100%", padding: s(6), boxSizing: "border-box", background: "#f0fdf4", border: `${s(1)}px solid #bbf7d0`, borderRadius: s(3) }}>
          <div style={{ fontSize: s(9), fontWeight: 600, color: "#15803d", marginBottom: s(3) }}>Attendance: 189 / 195 days</div>
          <div style={{ height: s(7), background: "#dcfce7", borderRadius: s(4), overflow: "hidden" }}>
            <div style={{ height: "100%", width: "97%", background: "#16a34a", borderRadius: s(4) }} />
          </div>
        </div>
      )

    // ── RMS compound types ───────────────────────────────────────────────────

    case "rms_campus_header": {
      // Try to show the banner image (per-campus stored in bannerByCampus)
      const byC     = rp.bannerByCampus as Record<string, any> | undefined
      const entry   = byC ? Object.values(byC)[0] as any : null
      const imgUrl  = entry?.url
      const s3Key   = imgUrl ? rmsUrlToKey(imgUrl) : null
      const imgSrc  = s3Key ? assetUrls[s3Key] : null
      if (s3Key && !imgSrc && onNeedUrl) onNeedUrl(s3Key)

      const gradient = rp.gradient as string | undefined
      const bg = gradient || (rp.bg && rp.bg !== "transparent" ? rp.bg : "#b59350")

      if (imgSrc) {
        return <img src={imgSrc} style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }} alt="" />
      }
      return (
        <div style={{
          width: "100%", height: "100%",
          background: bg, display: "flex",
          alignItems: "center", justifyContent: "center",
          padding: s(8), boxSizing: "border-box",
          overflow: "hidden",
        }}>
          <div style={{
            fontSize: s(rp.fontSize || 18),
            fontWeight: rp.fontWeight || 800,
            color: rp.color || "#fff",
            textAlign: "center", lineHeight: 1.3,
            textTransform: rp.textUpper ? "uppercase" : "none",
          }}>
            {tpl.schoolName || "The Eon Academy"}
          </div>
        </div>
      )
    }

    case "rms_campus_logo": {
      // Campus logo is fetched from the campus record at print time — show a styled placeholder
      const br  = rp.borderRadius ?? 1
      const bw  = rp.borderWidth  ?? 0
      const bc  = rp.borderColor !== "transparent" ? rp.borderColor : "#d1d5db"
      const bg2 = rp.bg && rp.bg !== "transparent" ? rp.bg : "#f1f5f9"
      return (
        <div style={{
          width: "100%", height: "100%",
          background: bg2,
          border: bw > 0 ? `${s(bw)}px solid ${bc}` : `${s(1)}px solid ${bc}`,
          borderRadius: br >= 0.9 ? "50%" : `${s(br * 10)}px`,
          display: "flex", alignItems: "center", justifyContent: "center",
          overflow: "hidden",
        }}>
          <i className="ti ti-building-school" style={{ fontSize: s(32), color: "#94a3b8" }} />
        </div>
      )
    }

    case "rms_photo": {
      const br  = rp.borderRadius ?? 2
      const bw  = rp.borderWidth  ?? 0.4
      const bc  = rp.borderColor || "#2e3192"
      const bg2 = rp.bg || "#f5f5f4"
      return (
        <div style={{
          width: "100%", height: "100%",
          background: bg2,
          border: `${s(bw)}px solid ${bc}`,
          borderRadius: `${s(br * 4)}px`,
          display: "flex", alignItems: "center", justifyContent: "center",
          overflow: "hidden",
        }}>
          <i className="ti ti-user-circle" style={{ fontSize: s(40), color: "#9ca3af" }} />
        </div>
      )
    }

    case "rms_grade_badge": {
      // Overall grade badge — concentric circles
      const passInner  = rp.passInnerColor  || "#bae6fd"
      const passOuter  = rp.passOuterColor  || "#0ea5e9"
      const letterCol  = rp.passLetterColor || "#eef2ff"
      const letterStk  = rp.passLetterStroke || "#1e3a8a"
      const grade      = rp.sampleGrade || "A"
      return (
        <div style={{
          width: "100%", height: "100%",
          background: passOuter, borderRadius: "50%",
          display: "flex", alignItems: "center", justifyContent: "center",
          overflow: "hidden",
        }}>
          <div style={{
            width: "72%", height: "72%",
            background: passInner, borderRadius: "50%",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <span style={{
              fontSize: s(layer.h * 0.38),
              fontWeight: 900,
              color: letterCol,
              WebkitTextStroke: `${s(0.8)}px ${letterStk}`,
              lineHeight: 1,
            }}>
              {grade}
            </span>
          </div>
        </div>
      )
    }

    case "rms_subject_table": {
      const variant   = rp.variant   // "styled" or undefined (default pill style)
      const headerBg2 = rp.headerBg  || rp.accentColor || "#004080"
      const headerCol = rp.headerColor || "#ffffff"
      const bodyBg2   = rp.bodyBg    || "#ffffff"
      const borderCol = rp.borderColor || "#cbd5e1"
      const rowLineC  = rp.rowLineColor || borderCol
      const totalBg2  = rp.totalBg   || "#ffffff"
      const totalCol  = rp.totalColor || headerBg2
      const fontSize2 = s(rp.fontSize || 8)
      const pad       = `${s(2.5)}px ${s(4)}px`
      const subName   = rp.name || "Subject"
      const showHead  = rp.showHeader !== false
      const showSubc  = rp.showSubcats !== false
      const pillCells = rp.pillCells || false
      const showTotal = rp.showTotal !== false
      const accentC   = rp.accentColor || headerBg2
      const accentDk  = rp.accentDarkColor || accentC
      const cardBorder = rp.cardBorderColor || "rgba(0,0,0,0.08)"

      // Sample subcategories
      const subcats = [
        { label: "Hands-On-Activities", ob: 25, tot: 25 },
        { label: "Verbal",              ob: 25, tot: 25 },
        { label: "Written",             ob: 42, tot: 50 },
      ]

      if (variant === "styled") {
        // Modern card style (Grade 1-5)
        return (
          <div style={{
            width: "100%", height: "100%", overflow: "hidden",
            border: `${s(0.5)}px solid ${cardBorder}`,
            borderRadius: s(3), background: bodyBg2,
            display: "flex", flexDirection: "column", fontSize: fontSize2,
          }}>
            {showHead && (
              <div style={{
                background: `linear-gradient(135deg, ${accentC} 0%, ${accentDk} 100%)`,
                color: "#fff", padding: `${s(3)}px ${s(5)}px`,
                fontWeight: 700, fontSize: s((rp.fontSize || 8) + 1),
                borderBottom: `${s(1)}px solid ${accentDk}`,
                letterSpacing: s(0.3),
              }}>
                {subName.replace(/\s*\(\d+sc\)/i, "").replace(/Subject \[\d+\]\s*/i, "")}
              </div>
            )}
            <div style={{ flex: 1, overflow: "hidden" }}>
              {showSubc && subcats.map((sc, i) => (
                <div key={i} style={{
                  display: "flex", alignItems: "center",
                  padding: `${s(2)}px ${s(4)}px`,
                  borderBottom: `${s(0.5)}px solid ${borderCol}`,
                  background: i % 2 === 0 ? "#fff" : "#f8fafc",
                }}>
                  <div style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sc.label}</div>
                  {showTotal && (
                    <div style={{ width: "32%", marginLeft: s(3) }}>
                      <div style={{ height: s(4), background: "#e2e8f0", borderRadius: s(2), overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${(sc.ob/sc.tot)*100}%`, background: accentC, borderRadius: s(2) }} />
                      </div>
                    </div>
                  )}
                  <div style={{ width: s(28), textAlign: "right", fontWeight: 600, color: accentC, flexShrink: 0, marginLeft: s(2) }}>
                    {sc.ob}/{sc.tot}
                  </div>
                </div>
              ))}
              <div style={{
                display: "flex", padding: `${s(2.5)}px ${s(4)}px`,
                background: `${accentC}18`, fontWeight: 700, color: totalCol,
                borderTop: `${s(0.5)}px solid ${accentC}40`,
              }}>
                <div style={{ flex: 1 }}>TOTAL</div>
                <div style={{ color: accentC }}>92/100</div>
              </div>
            </div>
          </div>
        )
      }

      // Default pill style (Playgroup, Montessori)
      return (
        <div style={{
          width: "100%", height: "100%", overflow: "hidden",
          border: `${s(0.5)}px solid ${borderCol}`,
          display: "flex", flexDirection: "column", fontSize: fontSize2,
          background: bodyBg2,
        }}>
          {showHead && (
            <div style={{
              background: headerBg2, color: headerCol,
              padding: `${s(3)}px ${s(5)}px`,
              fontWeight: 700, textTransform: rp.headerUpper ? "uppercase" : "none",
            }}>
              {subName.replace(/\s*\(\d+sc\)/i, "").replace(/Subject \[\d+\]\s*/i, "")}
            </div>
          )}
          <div style={{ flex: 1, overflow: "hidden" }}>
            {showSubc && subcats.map((sc, i) => (
              <div key={i} style={{
                display: "flex", alignItems: "center",
                borderBottom: `${s(0.5)}px solid ${rowLineC}`,
                padding: pad,
                background: i % 2 === 0 ? bodyBg2 : `${headerBg2}10`,
              }}>
                <div style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: rp.rowColor || "#1f2937" }}>{sc.label}</div>
                {showTotal && (
                  <div style={{ width: s(30), textAlign: "center", color: rp.rowColor || "#1f2937" }}>{sc.ob}</div>
                )}
                {showTotal && (
                  <div style={{ width: s(30), textAlign: "center", color: rp.rowColor || "#1f2937" }}>{sc.tot}</div>
                )}
                {pillCells && (
                  <div style={{
                    width: s(20), height: s(10), background: rp.pillColor || headerBg2,
                    borderRadius: s(5), display: "flex", alignItems: "center", justifyContent: "center",
                    border: `${s(0.5)}px solid ${rp.pillBorderColor || borderCol}`,
                    flexShrink: 0,
                  }} />
                )}
              </div>
            ))}
          </div>
          {showTotal && (
            <div style={{
              display: "flex", padding: pad,
              background: totalBg2, fontWeight: 700,
              borderTop: `${s(0.5)}px solid ${borderCol}`,
              color: totalCol,
            }}>
              <div style={{ flex: 1 }}>Total</div>
              <div>92/100</div>
            </div>
          )}
        </div>
      )
    }

    case "rms_grade_badge": {
      // Per-subject grade badge (pill/circle shown alongside subject_table)
      // Note: falls through from the outer grade_badge case above if already matched,
      // but the subject-level badge may have subjectIndex
      const passInner  = rp.passInnerColor  || "#bae6fd"
      const passOuter  = rp.passOuterColor  || "#0ea5e9"
      const letterCol  = rp.passLetterColor || "#eef2ff"
      const letterStk  = rp.passLetterStroke || "#1e3a8a"
      const grade2     = rp.sampleGrade || "A"
      return (
        <div style={{
          width: "100%", height: "100%",
          background: passOuter, borderRadius: "50%",
          display: "flex", alignItems: "center", justifyContent: "center",
          overflow: "hidden",
        }}>
          <div style={{
            width: "72%", height: "72%",
            background: passInner, borderRadius: "50%",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <span style={{
              fontSize: s(layer.h * 0.38),
              fontWeight: 900, color: letterCol,
              WebkitTextStroke: `${s(0.8)}px ${letterStk}`,
              lineHeight: 1,
            }}>{grade2}</span>
          </div>
        </div>
      )
    }

    case "rms_attendance_bar": {
      const barColor   = rp.barColor   || "#4ade80"
      const trackColor = rp.trackColor || "#e5e7eb"
      const labelCol   = rp.labelColor || "#2e3192"
      const borderCol2 = rp.borderColor || "#2e3192"
      const bRadius    = s((rp.borderRadius || 1.5) * 4)
      const label      = rp.labelText || "Attendance Journey"
      const showLabel  = rp.showLabel !== false
      const showRatio  = rp.showRatio !== false
      return (
        <div style={{
          width: "100%", height: "100%", padding: `${s(3)}px ${s(5)}px`,
          boxSizing: "border-box", display: "flex", flexDirection: "column",
          justifyContent: "center", gap: s(3),
          border: `${s(0.4)}px solid ${borderCol2}`,
          borderRadius: bRadius,
        }}>
          {showLabel && (
            <div style={{ fontSize: s(7.5), fontWeight: 700, color: labelCol }}>{label}</div>
          )}
          <div style={{ height: s(8), background: trackColor, borderRadius: s(4), overflow: "hidden" }}>
            <div style={{ height: "100%", width: "97%", background: barColor, borderRadius: s(4) }} />
          </div>
          {showRatio && (
            <div style={{ fontSize: s(7), color: labelCol, textAlign: "center" }}>189 / 195 Days</div>
          )}
        </div>
      )
    }

    case "rms_congrats": {
      const color2   = rp.color    || "#2e3192"
      const gradient = rp.gradient as string | undefined
      const fSize    = s(rp.fontSize || 14)
      const fWeight  = rp.fontWeight || 800
      const italic   = rp.italic || false
      const sample   = rp.sampleName ? `Congratulations, ${rp.sampleName}!` : "Congratulations!"
      const subText  = rp.sampleGrade ? `Grade Achieved: ${rp.sampleGrade}` : ""
      return (
        <div style={{
          width: "100%", height: "100%",
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          padding: s(6), boxSizing: "border-box", textAlign: "center",
        }}>
          <div style={{
            fontSize: fSize, fontWeight: fWeight,
            fontStyle: italic ? "italic" : "normal",
            background: gradient || "none",
            WebkitBackgroundClip: gradient ? "text" : "unset",
            WebkitTextFillColor: gradient ? "transparent" : color2,
            color: gradient ? "transparent" : color2,
            lineHeight: 1.3,
          }}>
            {sample}
          </div>
          {subText && (
            <div style={{ fontSize: s((rp.fontSize || 14) * 0.7), color: color2, marginTop: s(3), opacity: 0.8 }}>
              {subText}
            </div>
          )}
        </div>
      )
    }

    default: {
      // Unknown rms_* type — minimal labeled box
      const rmsType    = layer.type.startsWith("rms_") ? layer.type.slice(4) : layer.type
      const subjectName = typeof rp.name === "string" ? rp.name : ""
      return (
        <div style={{
          width: "100%", height: "100%",
          background: "#f1f5f9", border: `${s(1)}px solid #e2e8f0`,
          borderRadius: s(2), display: "flex", alignItems: "center",
          justifyContent: "center", padding: s(4), boxSizing: "border-box",
          textAlign: "center",
        }}>
          <span style={{ fontSize: s(9), fontWeight: 600, color: "#64748b", textTransform: "uppercase" }}>
            {rmsType.replace(/_/g, " ")}{subjectName ? ` — ${subjectName}` : ""}
          </span>
        </div>
      )
    }
  }
}
